/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Chip, Stack, Typography } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { OverridableStringUnion } from '@mui/types';
import get from 'lodash-es/get';
import React, { useState } from 'react';

import { contractFormText, contractStatusMap, contractStepLabels } from '../../constants/contracts';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import usePauseAllContracts from '../../hooks/usePauseAllContracts';
import useResumeAllContracts from '../../hooks/useResumeAllContracts';
import { useSnackBar } from '../../hooks/useSnackBar';
import useVersions, { ModelType } from '../../hooks/useVersions';
import SimpleStepper from '../forms/SimpleStepper';
import LookerLink from '../shared/LookerLink';
import ContractConfigurationBreadcrumb from './ContractConfigurationBreadcrumb';
import ContractForm from './ContractForm';
import ContractReportsPageLink from './ContractReportsPageLink';

type FocusedContractProps = {
  advertiserData?: any;
  activeStep: number;
};

interface ButtonPropsColorOverrides {}

// TODO:
// - unit tests
// - correctly handle reload on new contract submit
// - does this component need to exist?
//   - can we just distribute its functionality to the parent and child components?
function FocusedContract(props: FocusedContractProps) {
  const contractConfiguration = useContractConfigurationContext();
  const contractData = get(contractConfiguration, 'contractData');
  const { showSnackBar } = useSnackBar();
  const { activeStep, advertiserData } = props;
  const contractId = contractData.id;
  const contractStatus = contractData.derived_ui_state || 'incomplete';
  const [hasUnpausedCampaigns, setHasUnpausedCampaigns] = useState(contractData?.has_unpaused_campaigns);

  const { openVersionsModal } = useVersions(ModelType.Contract, Number(contractId));

  const togglePauseButtonText = hasUnpausedCampaigns ? contractFormText.pauseAll : contractFormText.resumeAll;
  const togglePauseButtonColor: OverridableStringUnion<
    'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
    ButtonPropsColorOverrides
  > = hasUnpausedCampaigns ? 'primary' : 'warning';
  const togglePauseVariant = hasUnpausedCampaigns ? 'text' : 'contained';
  const togglePauseButtonTextColor = hasUnpausedCampaigns ? undefined : '#fff';

  const pauseAllMutation = usePauseAllContracts(contractId);
  const resumeAllMutation = useResumeAllContracts(contractId);

  const togglePause = () => {
    if (hasUnpausedCampaigns) {
      pauseAllMutation.mutate(undefined, {
        onSuccess: () => {
          setHasUnpausedCampaigns(false);
          showSnackBar(`Contract ${contractData.name} was successfully paused.`, 'success', {
            event: 'contract_paused',
            contract_id: contractId,
            contract_name: contractData.name,
          });
        },
        onError: () => {
          showSnackBar(`Contract ${contractData.name} failed to pause.`, 'error', {
            event: 'contract_pause_error',
            contract_id: contractId,
            contract_name: contractData.name,
          });
        },
      });
    } else {
      resumeAllMutation.mutate(undefined, {
        onSuccess: () => {
          setHasUnpausedCampaigns(true);
          showSnackBar(`Contract ${contractData.name} was successfully resumed.`, 'success', {
            event: 'contract_resumed',
            contract_id: contractId,
            contract_name: contractData.name,
          });
        },
        onError: () => {
          showSnackBar(`Contract ${contractData.name} failed to resumed.`, 'error', {
            event: 'contract_resume_error',
            contract_id: contractId,
            contract_name: contractData.name,
          });
        },
      });
    }
  };

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 1,
          left: 0,
          zIndex: 10,
          backgroundColor: '#F9F9F9',
        }}
      >
        <ContractConfigurationBreadcrumb />
        <Stack>
          <Stack direction="row" mx={2} alignItems="center" justifyContent="space-between">
            <Stack spacing={3} direction="row" alignItems="center">
              <Typography variant="h5">
                {contractId ? contractFormText.contractHeader.edit : contractFormText.contractHeader.new}
              </Typography>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography variant="body2">{contractFormText.contractHeader.status}</Typography>
                <Chip
                  label={contractStatusMap[contractStatus][0]}
                  size="small"
                  color={contractStatusMap[contractStatus][2]}
                />
              </Stack>
              <Button variant="text" disabled={!contractId} onClick={openVersionsModal}>
                {contractFormText.changelog}
              </Button>
              <Button
                variant={togglePauseVariant}
                color={togglePauseButtonColor}
                disabled={!contractId}
                onClick={togglePause}
                size="small"
                sx={{ color: togglePauseButtonTextColor }}
              >
                {togglePauseButtonText}
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {contractId ? <ContractReportsPageLink contractId={contractId} /> : null}
              <LookerLink contractId={contractId} />
            </Stack>
          </Stack>
          <Stack px={1} py={2}>
            <SimpleStepper stepLabels={contractStepLabels} activeStep={activeStep} />
          </Stack>
        </Stack>
      </Stack>
      <ContractForm contractData={contractData || {}} existingAdvertiserData={advertiserData} />
    </>
  );
}

export default FocusedContract;
